import {
  BetBoxSetion,
  StyledButton,
  StyledButtonGroup,
  StyledInputLabel,
  StyledTextField,
  StopAtSection,
} from '@/components/features/casino/crash/BetBox/styles';
import { useTranslation } from 'next-i18next';
import { Add, Precision, Subtract } from '@/utils/decimal/operations';
import {
  autoBetState,
  autoCrashOutAtState,
  gameStatusState,
  stopAtState,
} from '@/components/features/casino/crash/state';
import { useAtom, useSetAtom, useAtomValue } from 'jotai';
import React, { useEffect, useMemo, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { GameStatus } from '@/components/features/casino/crash/types/crash';
import useDisableInput from '@/components/features/casino/crash/BetBox/useDisableInput';
import { MoonSwitch } from '@/components/ui/MoonSwitch';

interface FormFields {
  stopAt: number;
}

enum StopAtOperate {
  Add,
  Subtract,
}

export default function CrashStopAtAmount({ defaultAmount, onChange }) {
  const { t } = useTranslation();

  const minStopAt = 1.01;
  const defaultAutoCrashOutAtValue = 200;
  const [stopAt, setStopAt] = useState(defaultAmount);
  const setStopAtState = useSetAtom(stopAtState);
  const resetStopAtState = () => {
    setStopAtState(null);
  };
  useEffect(() => {
    onChange(stopAt);
  }, [stopAt]);
  const [autoCrashOutAt, setAutoCrashOutAt] = useAtom(autoCrashOutAtState);

  useEffect(() => {
    if (autoCrashOutAt) {
      setStopAt(stopAt ? stopAt : defaultAutoCrashOutAtValue);
    } else {
      setStopAt('');
      resetStopAtState();
    }
  }, [autoCrashOutAt, stopAt]);

  const handleStopAt = (operate: StopAtOperate) => {
    if (!autoCrashOutAt) return;
    const step = 1;
    let stopAtValue = stopAt;
    if (!stopAtValue) {
      stopAtValue = 1;
    }

    if (operate === StopAtOperate.Add) {
      if (stopAtValue === minStopAt) {
        stopAtValue = 1;
      }
      setStopAt(Add(stopAtValue, step, Precision.two));
    } else {
      setStopAt(
        Math.max(minStopAt, Subtract(stopAtValue, step, Precision.two)),
      );
    }
  };

  const controllerDisabled = !autoCrashOutAt;

  const materialUITextFieldProps = {
    sx: (theme) => ({
      [theme.breakpoints.down('md')]: {
        '& .MuiInputBase-root': {
          height: '30px',
          alignSelf: 'center',
          mr: 1,
        },
      },
    }),
  };

  return (
    <BetBoxSetion
      sx={{
        width: '172px',
      }}
    >
      <StyledInputLabel htmlFor="outCrash">
        {t('Auto Cash Out At')}
        <MoonSwitch
          sx={{ ml: 1 }}
          checked={autoCrashOutAt}
          onChange={(e) => setAutoCrashOutAt(e.target.checked)}
        />
      </StyledInputLabel>
      <StopAtSection>
        <NumericFormat
          disabled={controllerDisabled}
          inputMode="decimal"
          suffix={' x'}
          value={stopAt}
          onValueChange={(values) => {
            if (values.floatValue && values.floatValue >= minStopAt) {
              setStopAt(values.floatValue);
            }
          }}
          onBlur={(e) => {
            //border processing
            if (e.target.value === '') {
              setStopAt(defaultAutoCrashOutAtValue);
            } else {
              const amount = parseFloat(
                String(e.target.value).replace('x', '').trim(),
              );
              if (amount < minStopAt) {
                setStopAt(minStopAt);
              }
            }
          }}
          placeholder={
            controllerDisabled
              ? `${t('Cash out at')}`
              : `${defaultAutoCrashOutAtValue} x`
          }
          allowNegative={false}
          decimalScale={2}
          fixedDecimalScale={false}
          customInput={StyledTextField}
          {...materialUITextFieldProps}
        ></NumericFormat>
        <StyledButtonGroup
          sx={(theme) => ({
            cursor: controllerDisabled ? 'not-allowed' : '',
            mt: 1,
            [theme.breakpoints.down('md')]: {
              mt: 0,
              height: '52px',
            },
          })}
        >
          <StyledButton
            variant="outlined"
            color={'secondary'}
            disabled={controllerDisabled}
            onClick={() => handleStopAt(StopAtOperate.Subtract)}
          >
            -
          </StyledButton>
          <StyledButton
            variant="outlined"
            color={'secondary'}
            disabled={controllerDisabled}
            onClick={() => handleStopAt(StopAtOperate.Add)}
          >
            +
          </StyledButton>
        </StyledButtonGroup>
      </StopAtSection>
    </BetBoxSetion>
  );
}
